/*
  ET - Header search panel.

  The panel itself is a Drawer subclass under a new tag name, so it inherits Impact's focus trap, Escape handling,
  focus return to the trigger, aria-expanded mirroring on every [aria-controls] trigger and the reference counted
  body lock — none of which a hand rolled panel would get. Two getters change:

  - openFrom "top": Drawer's clip-path reveal then wipes downward from the panel's own top edge, which is the header's
    bottom edge, so the panel reads as the header growing. Under prefers-reduced-motion Impact already swaps the whole
    timeline for an opacity fade with no translation, which is exactly what the spec asks for.
  - shouldAppendToBody false: the panel stays inside the header section, so the section's custom properties keep
    inheriting into it and <predictive-search> is never disconnected and reconnected (its connectedCallback binds
    form listeners every time, and re-binding them would fire one fetch per previous open).

  The rest of this file is the keyboard layer and the client side matching for the quick answer / platform chips,
  which cannot come from search/suggest: it indexes products, collections, pages and articles only.
*/
(function () {
  'use strict';

  var READY_ATTRIBUTE = 'data-et-header-search-ready';
  if (document.documentElement.hasAttribute(READY_ATTRIBUTE)) return;
  document.documentElement.setAttribute(READY_ATTRIBUTE, '');

  var PANEL_ID = 'et-header-search';
  var RESULT_SELECTOR = '.et-header-search__link';

  if (window.customElements) {
    window.customElements.whenDefined('x-drawer').then(function () {
      if (window.customElements.get('et-search-panel')) return;

      var Drawer = window.customElements.get('x-drawer');

      window.customElements.define(
        'et-search-panel',
        class extends Drawer {
          get openFrom() {
            return 'top';
          }

          get shouldAppendToBody() {
            return false;
          }
        }
      );
    });

    /*
      Impact's PredictiveSearch._transitionToSlot animates every idle -> loading -> results swap with a 5px
      translateY through the Web Animations API, with no reduced-motion branch of its own (unlike Drawer, which has
      one). A WAAPI animation cannot be disabled from CSS, so the reduced-motion block in et-header-search.css is
      powerless over it and the panel's CONTENTS still translated. Replacing the method with the plain slot swap is
      the whole fix and needs no edit to theme.js.
    */
    window.customElements.whenDefined('predictive-search').then(function () {
      if (!window.matchMedia || !window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      var PredictiveSearch = window.customElements.get('predictive-search');
      if (!PredictiveSearch || PredictiveSearch.prototype._etReducedMotion) return;

      PredictiveSearch.prototype._etReducedMotion = true;
      PredictiveSearch.prototype._transitionToSlot = function (name) {
        var slot = this.shadowRoot && this.shadowRoot.firstElementChild;
        if (slot && slot.name !== name) slot.setAttribute('name', name);

        return Promise.resolve();
      };
    });
  }

  function panel() {
    return document.getElementById(PANEL_ID);
  }

  /* ---------------------------------------------------------------------------------------------------------- *
   *  Announcing the results
   *
   *  PredictiveSearch._doPredictiveSearch replaces the whole [slot="results"] subtree with a node parsed out of the
   *  fetch response, and it does so while the shadow <slot> is still named "loading". A live region rendered inside
   *  that subtree therefore arrives unslotted, unrendered and already carrying its text, which is the one case
   *  WAI-ARIA leaves undefined and no screen reader announces. So the region lives in the panel itself
   *  (snippets/et-header-search.liquid) and the count, localised server side, rides in on a data attribute.
   * ---------------------------------------------------------------------------------------------------------- */

  function announce(element, text) {
    var region = element.querySelector('[data-et-search-status]');
    if (!region || region.textContent === text) return;

    region.textContent = text;
  }

  function watchResults(element) {
    var host = element.querySelector('predictive-search');
    if (!host || host.hasAttribute('data-et-search-watched')) return;

    host.setAttribute('data-et-search-watched', '');

    new window.MutationObserver(function () {
      var slot = host.querySelector('[slot="results"][data-et-result-status]');
      announce(element, slot ? slot.getAttribute('data-et-result-status') : '');
    }).observe(host, { childList: true, subtree: true });
  }

  /* ---------------------------------------------------------------------------------------------------------- *
   *  "/" opens the panel from anywhere
   * ---------------------------------------------------------------------------------------------------------- */

  function isTyping(element) {
    if (!element) return false;
    if (element.isContentEditable) return true;

    var tag = element.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
  }

  document.addEventListener('keydown', function (event) {
    if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
    if (isTyping(event.target)) return;

    var element = panel();
    if (!element || typeof element.show !== 'function' || element.hasAttribute('open')) return;

    event.preventDefault();
    element.show();
  });

  /* ---------------------------------------------------------------------------------------------------------- *
   *  Up / down through the results, Enter opens the highlighted one
   * ---------------------------------------------------------------------------------------------------------- */

  function results(element) {
    return Array.prototype.filter.call(element.querySelectorAll(RESULT_SELECTOR), function (link) {
      return link.offsetParent !== null || link.getClientRects().length > 0;
    });
  }

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    var element = panel();
    if (!element || !element.hasAttribute('open') || !element.contains(event.target)) return;

    var links = results(element);
    if (links.length === 0) return;

    event.preventDefault();

    var current = links.indexOf(document.activeElement);
    var next;

    if (event.key === 'ArrowDown') {
      next = current === -1 ? 0 : (current + 1) % links.length;
    } else {
      next = current <= 0 ? links.length - 1 : current - 1;
    }

    links[next].focus();
  });

  /* ---------------------------------------------------------------------------------------------------------- *
   *  Suggestion chips fill the input and run the same predictive search a typed query would
   * ---------------------------------------------------------------------------------------------------------- */

  function input(element) {
    return element.querySelector('input[name="q"]');
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var chip = target.closest('[data-et-search-chip]');
    if (!chip) return;

    var element = panel();
    if (!element || !element.contains(chip)) return;

    var field = input(element);
    if (!field) return;

    event.preventDefault();
    field.value = chip.getAttribute('data-et-search-chip') || chip.textContent.trim();
    field.focus();
    field.dispatchEvent(new Event('input', { bubbles: true }));
    match(element, field.value);
  });

  /* ---------------------------------------------------------------------------------------------------------- *
   *  Quick answers and platform chips
   *
   *  These live outside <predictive-search>, so the idle / loading / results slot swap never hides them, and they
   *  are matched here rather than server side: search/suggest does not index section block settings, and the
   *  fetch target section cannot see the header's blocks.
   * ---------------------------------------------------------------------------------------------------------- */

  function match(element, query) {
    var group = element.querySelector('[data-et-search-answers]');
    if (!group) return;

    /* In the theme editor every quick answer and platform chip stays visible, or a merchant could never select one. */
    if (window.Shopify && window.Shopify.designMode) {
      group.hidden = false;
      Array.prototype.forEach.call(group.querySelectorAll('[data-et-keywords]'), function (item) {
        item.hidden = false;
      });
      return;
    }

    var normalised = (query || '').trim().toLowerCase();
    var shown = 0;

    Array.prototype.forEach.call(group.querySelectorAll('[data-et-keywords]'), function (item) {
      var hit = false;

      if (normalised !== '') {
        var keywords = item.getAttribute('data-et-keywords').split(',');

        for (var i = 0; i < keywords.length; i++) {
          var keyword = keywords[i].trim().toLowerCase();
          if (keyword !== '' && normalised.indexOf(keyword) !== -1) {
            hit = true;
            break;
          }
        }
      }

      item.hidden = !hit;
      if (hit) shown += 1;
    });

    group.hidden = shown === 0;
  }

  document.addEventListener('input', function (event) {
    var field = event.target;
    if (!field || field.name !== 'q') return;

    var element = panel();
    if (!element || !element.contains(field)) return;

    match(element, field.value);
  });

  /* A reset button empties the field without firing `input`. */
  document.addEventListener('reset', function (event) {
    var element = panel();
    if (!element || !element.contains(event.target)) return;

    window.setTimeout(function () {
      match(element, '');
      announce(element, '');
    }, 0);
  });

  /* Start from a clean slate every time the panel opens. */
  document.addEventListener('toggle', function (event) {
    var element = event.target;
    if (!element || element.id !== PANEL_ID || !element.hasAttribute('open')) return;

    var field = input(element);
    match(element, field ? field.value : '');
    announce(element, '');
    watchResults(element);
  });

  /* ---------------------------------------------------------------------------------------------------------- *
   *  Init
   * ---------------------------------------------------------------------------------------------------------- */

  function init() {
    var element = panel();
    if (element) watchResults(element);
  }

  init();
  document.addEventListener('shopify:section:load', init);
})();
